import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@ncoder/ui";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";

const MAX_SELECTION = 10;

interface Props {
  /** Every type the user can choose from (all 57 org types when known). */
  options: string[];
  /** Currently selected types (max MAX_SELECTION). */
  value: string[];
  onChange: (next: string[]) => void;
  /** Optional list of types that have findings — bolded in the dropdown. */
  highlighted?: Set<string>;
  /** When all selected → considered "default" so the clear button appears. */
  defaultValue?: string[];
  className?: string;
}

/**
 * Multi-select metadata type picker.
 *
 * Up to 10 selections. The trigger shows count or compact chip summary, and
 * an inline X clears the selection back to `defaultValue` (caller decides what
 * default looks like — typically the set of types with findings in the scan).
 */
export function MetadataTypeMultiSelect({
  options, value, onChange, highlighted, defaultValue, className,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.toLowerCase().includes(q));
  }, [options, query]);

  const selectedSet = useMemo(() => new Set(value), [value]);
  const atCap = value.length >= MAX_SELECTION;
  const isDefault =
    defaultValue !== undefined &&
    defaultValue.length === value.length &&
    defaultValue.every((d) => selectedSet.has(d));

  const toggle = (t: string) => {
    if (selectedSet.has(t)) {
      onChange(value.filter((x) => x !== t));
    } else if (atCap) {
      // ignore — at cap
    } else {
      onChange([...value, t]);
    }
  };

  const clear = () => onChange(defaultValue ?? []);
  const selectAllVisible = () => {
    const next = new Set(value);
    for (const t of filtered) {
      if (next.size >= MAX_SELECTION) break;
      next.add(t);
    }
    onChange(Array.from(next));
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className={cn("h-9 gap-2 min-w-[14rem] justify-between", className)}>
          <span className="truncate text-left">
            {value.length === 0
              ? "Metadata types"
              : value.length === 1
              ? value[0]
              : `${value.length} selected`}
          </span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[22rem] p-0">
        <div className="p-3 border-b space-y-2">
          <Input
            placeholder="Search types…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-8"
            autoFocus
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {value.length} / {MAX_SELECTION} selected
              {atCap ? " (max)" : ""}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="underline hover:text-foreground disabled:opacity-40 disabled:no-underline"
                onClick={selectAllVisible}
                disabled={atCap || filtered.length === 0}
              >
                add visible
              </button>
              <button
                type="button"
                className="underline hover:text-foreground disabled:opacity-40 disabled:no-underline"
                onClick={clear}
                disabled={isDefault && value.length > 0}
              >
                clear
              </button>
            </div>
          </div>
        </div>
        <div className="max-h-72 overflow-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-3 py-6 text-xs text-muted-foreground text-center">
              No types match "{query}"
            </div>
          ) : (
            filtered.map((t) => {
              const checked = selectedSet.has(t);
              const isHighlighted = highlighted?.has(t);
              const disabled = !checked && atCap;
              return (
                <label
                  key={t}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-muted",
                    disabled && "opacity-50 cursor-not-allowed hover:bg-transparent"
                  )}
                  onClick={(e) => {
                    if (disabled) e.preventDefault();
                  }}
                >
                  <Checkbox
                    checked={checked}
                    onCheckedChange={() => !disabled && toggle(t)}
                    disabled={disabled}
                  />
                  <span className={cn("flex-1 truncate", isHighlighted && "font-medium")}>
                    {t}
                  </span>
                  {isHighlighted ? (
                    <Badge variant="outline" className="text-[10px] h-5 px-1">
                      in scan
                    </Badge>
                  ) : null}
                  {checked ? <Check className="h-3.5 w-3.5 text-primary" /> : null}
                </label>
              );
            })
          )}
        </div>
        {value.length > 0 ? (
          <div className="p-3 border-t flex flex-wrap gap-1.5">
            {value.map((t) => (
              <Badge
                key={t}
                variant="secondary"
                className="text-[10px] gap-1 px-2 py-0.5 cursor-pointer"
                onClick={() => toggle(t)}
                title={`Remove ${t}`}
              >
                {t}
                <X className="h-3 w-3" />
              </Badge>
            ))}
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

export default MetadataTypeMultiSelect;
export { MAX_SELECTION as METADATA_TYPE_MAX_SELECTION };
